import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { MarketResearchHandoff as Handoff, saveMarketResearchHandoff } from '../api/stageFunctions';
import { toUserFacingError } from '../utils/userFacingError';

const statusText = (value: string) => value.replaceAll('_', ' ').replace(/^./, character => character.toUpperCase());

/** Show report readiness, currency, and saved file references. */
export const MarketResearchHandoffStatus = ({ handoff }: { handoff: Handoff }) => <Box data-testid="mrr-handoff-status">
  {handoff.research_status !== 'complete' && <Typography>{handoff.research_status === 'unknown' ? 'Research completion has not been confirmed.' : 'Research is still in progress.'}</Typography>}
  {handoff.context_stale && <Alert severity="warning" sx={{ mt: 1 }}>Research or acquisition details have changed. This saved report is retained; review it before use.</Alert>}
  {Object.entries(handoff.contracting_records).some(([, reference]) => reference.trim()) && <Box sx={{ mt: 1 }}><Typography variant="subtitle2">File references</Typography>{Object.entries(handoff.contracting_records).filter(([, reference]) => reference.trim()).map(([key, reference]) => <Box key={key}><Typography variant="caption">{statusText(key)}</Typography><Typography variant="body2">{reference}</Typography></Box>)}</Box>}
</Box>;

export const MarketResearchHandoffControls = ({ handoff, flowId, isViewOnly, onUpdate }: {
  handoff: Handoff; flowId?: string | null; isViewOnly: boolean; onUpdate: (next: Handoff) => void;
}) => {
  const [records, setRecords] = useState<Record<string, string>>({ ...handoff.contracting_records });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const contextRef = useRef({ handoff, flowId, isViewOnly, onUpdate });
  contextRef.current = { handoff, flowId, isViewOnly, onUpdate };
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setRecords({ ...contextRef.current.handoff.contracting_records });
    setSaving(false); setError('');
    return () => { generation.current += 1; };
  }, [flowId]);
  const canEdit = !isViewOnly && handoff.can_edit_profile && Boolean(flowId);
  const saveRecords = async () => {
    const context = contextRef.current;
    if (context.isViewOnly || !context.handoff.can_edit_profile || !context.flowId || inFlight.current) return;
    const requestGeneration = generation.current;
    inFlight.current = true; setSaving(true); setError('');
    try {
      const next = await saveMarketResearchHandoff(context.flowId, { revision: context.handoff.revision, contracting_records: records, capture: true });
      if (requestGeneration !== generation.current || contextRef.current.flowId !== context.flowId) return;
      contextRef.current = { ...contextRef.current, handoff: next };
      contextRef.current.onUpdate(next);
    } catch (caught) { if (requestGeneration === generation.current) setError(toUserFacingError(caught)); }
    finally { inFlight.current = false; if (requestGeneration === generation.current) setSaving(false); }
  };
  return <Box sx={{ mt: 2 }}>
    <MarketResearchHandoffStatus handoff={handoff} />
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {canEdit && handoff.procedure === 'schedule_order' && <Box component="details" sx={{ mt: 2 }}><Box component="summary">Schedule file references</Box>
      <Typography variant="body2">Add file references as they become available. A file reference does not record approval.</Typography>
      <Grid container spacing={1.5} sx={{ mt: 1 }}>{[
        ['schedule_contract', 'Schedule contract reference'], ['rfq_basis', 'RFQ basis record reference'], ['quotation_interchanges', 'Quotation interchanges record reference'],
        ['technical_fit', 'Technical fit record reference'], ['period_option_pricing', 'Period and option pricing record reference'], ['best_value_basis', 'Best-value basis record reference'],
        ['award_decision', 'Award decision record reference'], ['olm_price_determination', 'OLM price determination record reference'],
      ].filter(([key]) => key !== 'olm_price_determination' || [...handoff.available_records, ...handoff.outstanding_actions].some(record => record.key === key)).map(([key, label]) => <Grid item xs={12} md={6} key={key}><TextField fullWidth label={label} value={records[key] ?? ''} disabled={saving} onChange={event => setRecords(current => ({ ...current, [key]: event.target.value }))} /></Grid>)}</Grid>
      <Button disabled={saving} onClick={() => void saveRecords()}>Save schedule file references</Button>
    </Box>}
  </Box>;
};
